<template>
    
    <!-- Card Format -->
    <div v-if="cardFormat" class="group overflow-hidden relative type-primary rounded-lg p-4 border border-keyline-primary hover:border-keyline-primary-invert bg-gray-tier-4 hover:bg-gray-tier-3 h-[252px]">
        <button
            @click.stop.prevent="getResource($event, res)"
            tabindex="-1"
            class="overflow-hidden w-full flex flex-col"
        >
            <img
                :src="thumbnail"
                :alt="res.name"
                class="object-cover opacity-90 w-full group-hover:opacity-100 rounded-lg h-32"
            />
        </button>

        <div class="relative">
            <span class="type-secondary text-sm text-left pt-3 block">
                {{ vendorName }}
            </span>
            <button
                class="overflow-hidden type-secondary text-sm max-w-[90%] text-left underline-offset-4 hover:underline decoration-1"
                v-if="vendorName"
                @click.stop.prevent="getResource($event, res)"
                tabindex="-1"
            >
                
                <div class="type-primary font-medium text-base">
                    {{ res.name }}
                </div>
            </button>
            <template v-if="user">
                <div class="hidden group-hover:flex absolute top-1 right-0">
                    <button 
                        @click="toggle"
                        class="leading-none flex items-center justify-center p-1 w-9 h-9 hover:bg-gray-tier-4 rounded-full"
                    >
                        <BaseSvg name="icon-more-vert" class="h-4 w-4" />
                    </button>
                    <Popover ref="op" v-model:visible="optionsVisible" :pt="{
                        transition: {
                            enterActiveClass: 'menu-fade-enter-active',
                            enterFromClass: 'menu-fade-enter-from',
                            enterToClass: 'menu-fade-enter-to',
                            leaveActiveClass: 'menu-fade-leave-active',
                            leaveFromClass: 'menu-fade-leave-from',
                            leaveToClass: 'menu-fade-leave-to'
                        }
                    }">
                        <div class="flex flex-col gap-4 w-fit h-10 text-sm">
                            <Pin type="resource" :id="res.id" :label="true" class="w-44"/>
                        </div>
                    </Popover>
                </div>
            </template>
        </div>
    </div>
    
    <!-- Table Format -->
    <div
        v-else
        class="group relative col-span-full w-full grid grid-cols-[48px_1fr_36px] at1200:grid-cols-[48px_1fr_7rem_7rem_36px] gap-4 items-center px-3 py-2 border-b border-keyline-primary hover:bg-gray-tier-5"
    >
        <!-- Left column – thumbnail or icon -->
        <button
            @click.stop.prevent="getResource($event, res)"
            tabindex="-1"
            class="flex-shrink-0 w-12 h-12 flex items-center justify-center overflow-hidden rounded"
        >
            <img
                v-if="res.asset.source === 4 && thumbnail"
                :src="thumbnail"
                :alt="res.name"
                class="object-cover w-full h-full"
            />
            <BaseSvg
                v-else
                :name="resourceTypeIcon"
                class="w-8 h-8 type-tertiary"
            />
        </button>

        <!-- Middle column – vendor name and resource name -->
        <div class="overflow-hidden">
            <button
                v-if="vendorName"
                @click.stop.prevent="getResource($event, res)"
                tabindex="-1"
                class="type-secondary text-left text-sm"
            >
                {{ vendorName }}
                <div class="type-primary font-medium text-base underline-offset-4 hover:underline decoration-1">
                    {{ res.name }}
                </div>
            </button>
        </div>

        <div class="hidden at1200:block w-24 leading-tight type-secondary">
            {{ res.facet_types[0] }}
        </div>

        <!-- Updated At column -->
        <div class="hidden at1200:block type-tertiary whitespace-nowrap text-right">
            {{ updatedAt ? DateTime.fromISO(updatedAt).toLocaleString(DateTime.DATE_MED) : "–" }}
        </div>

        <!-- Right column – actions popover -->
        <template v-if="user">
            <div class="flex justify-end w-9 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto">
                <button
                    @click="toggle"
                    class="leading-none flex items-center justify-center p-1 w-9 h-9 hover:bg-gray-tier-4 rounded-full"
                    :class="{
                        'bg-gray-tier-4': optionsVisible,
                    }"
                >
                    <BaseSvg name="icon-more-vert" class="h-4 w-4" />
                </button>
                <Popover
                    ref="op"
                    v-model:visible="optionsVisible"
                    :pt="{
                        transition: {
                            enterActiveClass: 'menu-fade-enter-active',
                            enterFromClass: 'menu-fade-enter-from',
                            enterToClass: 'menu-fade-enter-to',
                            leaveActiveClass: 'menu-fade-leave-active',
                            leaveFromClass: 'menu-fade-leave-from',
                            leaveToClass: 'menu-fade-leave-to'
                        }
                    }"
                >
                    <div class="flex flex-col gap-4 w-fit h-10">
                        <Pin type="resource" :id="res.id" :label="true" class="w-44" />
                    </div>
                </Popover>
            </div>
        </template>
    </div>
</template>

<script setup>
import {computed, onMounted, ref} from "vue";
import {BaseSvg, Pin, TableActionsButton} from "@/Components";
import {eventBus, useAssetTransforms, useResources, useUser} from "@/Composables";
import Popover from 'primevue/popover';
import { DateTime } from 'luxon';

const { user } = useUser();
const { getResource, transformElasticSearchResource } = useResources();

const props = defineProps({
    resource: {
        type: Object,
        required: true,
    },
    elasticsearch: {
        type: Boolean,
        required: false,
        default: false,
    },
    cardFormat: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const { marketingAssetPdfThumbnail, youTubeThumbnail } = useAssetTransforms();

const optionsVisible = ref(false);

const toggleOptions = () => {
    optionsVisible.value = !optionsVisible.value;
    eventBus.emit('resource:options-toggled',res.value.id)
};

const onOptionsToggled = (resource_id) => {
    if(resource_id !== res.value.id) {
        close()
    }
};

const close = () => {
    optionsVisible.value = false
};

const res = computed(() => {
    if (props.elasticsearch) {
        return transformElasticSearchResource(props.resource);
    } else {
        return props.resource;
    }
});

const thumbnail = computed(() => {
    switch (res.value.asset.source) {
        case 3:
            return marketingAssetPdfThumbnail(
                res.value.asset.external_id,
                "c_fill,g_north,h_500,w_1000"
            );
        case 4:
            return youTubeThumbnail(res.value.asset.external_id);
        default:
            return null;
    }
});

const vendorName = computed(() => {
    const vendor = res.value.vendors?.[0] || {};
    return vendor.display_name ?? vendor.name ?? null;
});

const updatedAt = computed(() => {
    if (props.elasticsearch) {
        return props.resource.updated_at ?? null;
    }
    return res.value.asset.updated_at ?? null;
});

const resourceTypeIcon = computed(() => {
    switch (res.value.asset.source) {
        case 3:
            return "icon-document";
        case 4:
            return "icon-movie";
        default:
            return null;
    }
});

onMounted(() => {
    eventBus.on('resource:options-toggled', onOptionsToggled);

    eventBus.on('pins:pin-toggled-on', close);
    eventBus.on('pins:pin-toggled-off', close);
})

//dropdown
const op = ref();
const toggle = (event) => {
    eventBus.emit('resource:options-toggled', res.value.id);
    op.value.toggle(event);
}

</script>

<style>
/* .vendor-name {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
} */

.p-popover:after , .p-popover:before {
    display: none !important;
}
</style>
